import { CheckCircle2, ChevronRight, Clock, RotateCcw, XCircle } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaMerchantMark } from "@/shared/components/data-display/ColocaMerchantMark";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { merchantBrandOf } from "@/shared/utils/merchantBrands";

import { OperationType, TransactionStatus, type CardTransaction } from "../types/card-movements.types";
import { formatMovementDateTime } from "../utils/cardMovementHelpers";
import { getTransactionTypeMeta } from "../utils/cardTransactionDetailHelpers";
import {
  formatPlainAmount,
  formatSignedAmount,
  resolveUsdAmount,
  shouldShowLocalAmount,
} from "../utils/transactionAmountFormat";

type CardMovementRowProps = {
  movement: CardTransaction;
  onClick?: (transactionId: string) => void;
  /** Just arrived by socket: tinted for a moment so the eye finds it. */
  highlighted?: boolean;
};

const STATUS_META: Record<
  TransactionStatus,
  {
    variant: "success" | "warning" | "destructive" | "secondary";
    icon: LucideIcon;
    tKey: string;
  }
> = {
  COMPLETED: {
    variant: "success",
    icon: CheckCircle2,
    tKey: "movements_status_completed",
  },
  PENDING: {
    variant: "warning",
    icon: Clock,
    tKey: "movements_status_pending",
  },
  FAILED: {
    variant: "destructive",
    icon: XCircle,
    tKey: "movements_status_failed",
  },
  REVERSED: {
    variant: "secondary",
    icon: RotateCcw,
    tKey: "movements_status_reversed",
  },
};

/**
 * One card movement as a pressable row: the full list in the Movements tab and the latest three in the
 * Overview render this same row, so the two can never describe a movement differently.
 *
 * Lays out against the nearest `@container` (the list around it), not the viewport: next to the 380px card
 * sidebar the column is narrow on a laptop too, and the fixed status and amount columns left the merchant no
 * room.
 */
export const CardMovementRow = ({ movement: tx, onClick, highlighted = false }: CardMovementRowProps) => {
  const { t } = useTranslations("cards");

  const meta = getTransactionTypeMeta(tx.transaction_type, tx.top_up_source);
  const Icon = meta.icon;
  const statusMeta = STATUS_META[tx.status];
  const StatusIcon = statusMeta.icon;
  const merchant = tx.merchant_name || tx.description || "—";

  const usdAmount = resolveUsdAmount(tx);
  const showLocal = shouldShowLocalAmount(tx);

  return (
    <button
      type="button"
      onClick={() => onClick?.(tx.id)}
      className={cn(
        "flex w-full items-center gap-3 p-3.5 text-left transition-colors duration-700 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        highlighted && "bg-primary/10",
      )}
    >
      {/* A known brand shows its mark; any other movement keeps the icon of its type. */}
      {merchantBrandOf(tx.merchant_name) ? (
        <ColocaMerchantMark merchant={tx.merchant_name} />
      ) : (
        <span
          className={cn(
            "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-md)]",
            meta.iconWrapper,
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">{merchant}</div>
        <div className="truncate text-xs text-muted-foreground">{formatMovementDateTime(tx.created_at)}</div>
        <div className="mt-1 @lg:hidden">
          <ColocaBadge variant={statusMeta.variant} icon={StatusIcon}>
            {t(statusMeta.tKey as never)}
          </ColocaBadge>
        </div>
      </div>
      <div className="hidden w-28 shrink-0 justify-start @lg:flex">
        <ColocaBadge variant={statusMeta.variant} icon={StatusIcon}>
          {t(statusMeta.tKey as never)}
        </ColocaBadge>
      </div>
      <div className="flex shrink-0 flex-col items-end text-right @lg:w-32">
        <span
          className={cn(
            "inline-flex items-baseline gap-1 font-mono text-sm font-medium tabular-nums",
            tx.operation_type === OperationType.CREDIT
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-foreground",
            tx.status === TransactionStatus.FAILED && "text-muted-foreground line-through",
          )}
        >
          {usdAmount != null
            ? formatSignedAmount(usdAmount, "USD", tx.operation_type)
            : formatSignedAmount(tx.amount, tx.currency, tx.operation_type)}
          <span className="text-[10px] font-normal text-muted-foreground">USD</span>
        </span>
        {showLocal && (
          <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
            {formatPlainAmount(tx.amount, tx.currency)}
          </span>
        )}
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
    </button>
  );
};
